<template>
  <!-- Reuses .product-options styling so it visually matches Color / Size / Inseam selectors -->
  <div
    v-if="styles.length"
    ref="rootEl"
    class="product-options program-styles"
    :data-stio-program-handle="programHandle"
    :data-stio-style-count="styles.length"
  >
    <div class="product-options__option">
      <div class="product-options__title">
        <span>{{ heading.toUpperCase() }} -</span>
        <div class="product-options__title--selected">
          <span>{{ selectedLabel }}</span>
        </div>
      </div>

      <div class="product-options__other-options">
        <template v-for="(style, index) in styles" :key="index">
          <!-- Current style: static, not a link -->
          <span
            v-if="style.current"
            class="product-options__item selected"
            aria-current="true"
          >
            <span class="product-options__item-name">{{ style.label }}</span>
          </span>

          <!-- Other styles: navigate to their PDP (whole pill is clickable) -->
          <a
            v-else
            :href="style.url"
            class="product-options__item"
            data-stio-gtm-id="PDP: Program Style"
            :data-stio-program-handle="programHandle"
            :data-stio-from-style="selectedLabel"
            :data-stio-to-style="style.label"
            :data-stio-to-handle="style.handle"
            :data-stio-to-available="String(style.available)"
            :data-stio-style-position="index + 1"
            :data-stio-style-count="styles.length"
            :data-stio-product-handle="product.handle"
            :data-stio-product-name="product.title"
            :aria-label="`View ${style.label}`"
            @pointerenter="onStylePrefetch(style)"
            @click="onStyleClick($event, style)"
          >
            <span class="product-options__item-name">{{ style.label }}</span>
          </a>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, toRefs, onMounted, onUnmounted } from 'vue';

  const props = defineProps({
    program: {
      type: Object,
      default: () => ({}),
    },
    product: {
      type: Object,
      default: () => ({}),
    },
  });

  const { program } = toRefs(props);

  // Fixed heading word for the selector, e.g. "STYLE - Hooded Jacket"
  const heading = 'Style';

  const styles = computed(() => program.value?.styles || []);

  const programHandle = computed(() => program.value?.handle || '');

  // Show the current product's own style label as the selected value.
  // Prefer current_label (always the product being viewed); fall back to the
  // style flagged as current in the list.
  const selectedLabel = computed(() => {
    if (program.value?.current_label) return program.value.current_label;
    const current = styles.value.find((style) => style.current);
    return current ? current.label : '';
  });

  // Switch styles in-place (no full page redirect). The href stays intact so
  // middle-click / cmd-click / no-JS all still open the PDP normally.
  const onStyleClick = (event, style) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }
    event.preventDefault();
    window.dispatchEvent(
      new CustomEvent('pdp:switch-style', {
        detail: { url: style.url, handle: style.handle, label: style.label },
      })
    );
  };

  const prefetch = (url) => {
    if (url) {
      window.dispatchEvent(new CustomEvent('pdp:prefetch-style', { detail: { url } }));
    }
  };

  // Prefetch the target product's data on hover/touch so the click feels instant.
  const onStylePrefetch = (style) => prefetch(style.url);

  // Cap how many siblings we pre-warm on load so a large program doesn't fire a
  // burst of full-page fetches. Hover still prefetches anything beyond the cap.
  const PREFETCH_LIMIT = 6;

  // --- Impression tracking ---------------------------------------------------
  // GTM's Element Visibility trigger can't re-observe the selector after an
  // in-place style switch replaces it, so we push the impression ourselves.
  // The buy box remounts on every switch, so one push per mount is exactly one
  // per product view — the denominator for the click-through rate.
  const rootEl = ref(null);
  let impressionObserver = null;
  let userDataTimer = null;

  // Elevar pushes dl_user_data ahead of its page_view. GA4 mis-attributes
  // events that arrive before the page_view, so the impression waits for it.
  const ELEVAR_USER_DATA_EVENT = 'dl_user_data';
  const USER_DATA_POLL_MS = 100;
  const USER_DATA_TIMEOUT_MS = 5000;

  const hasUserData = () =>
    Array.isArray(window.dataLayer) &&
    window.dataLayer.some((entry) => entry?.event === ELEVAR_USER_DATA_EVENT);

  const pushImpression = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'program_style_impression',
      program_handle: programHandle.value,
      style_count: styles.value.length,
    });
  };

  const clearUserDataTimer = () => {
    if (userDataTimer) {
      clearInterval(userDataTimer);
      userDataTimer = null;
    }
  };

  // On the first page view dl_user_data may not have landed yet, so poll for it.
  // After an in-place style switch it is already there and this pushes straight
  // away. The timeout is a safety net: if Elevar never loads (blocked, failed
  // request) we'd rather record the impression late than lose it.
  const queueImpression = () => {
    if (hasUserData()) {
      pushImpression();
      return;
    }

    let waited = 0;
    clearUserDataTimer();
    userDataTimer = setInterval(() => {
      waited += USER_DATA_POLL_MS;
      if (!hasUserData() && waited < USER_DATA_TIMEOUT_MS) return;
      clearUserDataTimer();
      pushImpression();
    }, USER_DATA_POLL_MS);
  };

  const stopObservingImpression = () => {
    if (impressionObserver) {
      impressionObserver.disconnect();
      impressionObserver = null;
    }
  };

  // Fire once the selector is at least half on screen, matching what the GTM
  // visibility trigger would have done — so a selector the shopper never
  // scrolls to isn't counted as an impression.
  const observeImpression = () => {
    if (!rootEl.value) return;

    if (!('IntersectionObserver' in window)) {
      queueImpression();
      return;
    }

    impressionObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        queueImpression();
        stopObservingImpression();
      },
      { threshold: 0.5 }
    );
    impressionObserver.observe(rootEl.value);
  };

  // Warm the cache for sibling styles once the browser is idle, so the very
  // first click is instant too (not just clicks preceded by a hover).
  onMounted(() => {
    observeImpression();

    const warmAll = () => {
      styles.value
        .filter((style) => !style.current)
        .slice(0, PREFETCH_LIMIT)
        .forEach((style) => prefetch(style.url));
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(warmAll, { timeout: 2500 });
    } else {
      setTimeout(warmAll, 1200);
    }
  });

  onUnmounted(() => {
    stopObservingImpression();
    clearUserDataTimer();
  });
</script>
